import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Check, X, Shield, Zap, RefreshCw, 
  DollarSign, Calendar, Layers, Tag, Info, AlertCircle, CheckCircle2, PowerOff, CheckCircle
} from 'lucide-react';
import AdminLayout from '../common/AdminLayout';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../../utils/api';

export interface SubscriptionPlan {
  id: number;
  plan_name: string;
  description: string;
  amount: number;
  currency: string;
  billing_interval: 'monthly' | 'yearly' | string;
  features: string[];
  paddle_product_id?: string;
  paddle_price_id?: string;
  status: 'active' | 'inactive' | string;
  created_at?: string;
  updated_at?: string;
}

const APPLICATION_ACCESS_OPTIONS = [
  'Unlimited service listings',
  'Verified badge display',
  'Phone number visibility',
  'Enhanced profile visibility',
  'Direct customer messaging & inquiries',
  'Priority customer support',
  'Full access'
];

const PADDLE_CURRENCIES = [
  { code: 'USD', name: 'US Dollar ($)' },
  { code: 'EUR', name: 'Euro (€)' },
  { code: 'GBP', name: 'Pound Sterling (£)' },
  { code: 'JPY', name: 'Japanese Yen (¥)' },
  { code: 'AUD', name: 'Australian Dollar (A$)' },
  { code: 'CAD', name: 'Canadian Dollar (C$)' },
  { code: 'CHF', name: 'Swiss Franc (CHF)' },
  { code: 'HKD', name: 'Hong Kong Dollar (HK$)' },
  { code: 'SGD', name: 'Singapore Dollar (S$)' },
  { code: 'SEK', name: 'Swedish Krona (kr)' },
  { code: 'ARS', name: 'Argentine Peso ($)' },
  { code: 'BRL', name: 'Brazilian Real (R$)' },
  { code: 'CLP', name: 'Chilean Peso ($)' },
  { code: 'CNY', name: 'Chinese Yuan (¥)' },
  { code: 'COP', name: 'Colombian Peso ($)' },
  { code: 'CZK', name: 'Czech Koruna (Kč)' },
  { code: 'DKK', name: 'Danish Krone (kr)' },
  { code: 'HUF', name: 'Hungarian Forint (Ft)' },
  { code: 'ILS', name: 'Israeli Shekel (₪)' },
  { code: 'INR', name: 'Indian Rupee (₹)' },
  { code: 'KRW', name: 'South Korean Won (₩)' },
  { code: 'MXN', name: 'Mexican Peso ($)' },
  { code: 'NOK', name: 'Norwegian Krone (kr)' },
  { code: 'NZD', name: 'New Zealand Dollar (NZ$)' },
  { code: 'PEN', name: 'Peruvian Sol (S/.)' },
  { code: 'PLN', name: 'Polish Zloty (zł)' },
  { code: 'RUB', name: 'Russian Ruble (₽)' },
  { code: 'THB', name: 'Thai Baht (฿)' },
  { code: 'TRY', name: 'Turkish Lira (₺)' },
  { code: 'TWD', name: 'New Taiwan Dollar (NT$)' },
  { code: 'UAH', name: 'Ukrainian Hryvnia (₴)' },
  { code: 'VND', name: 'Vietnamese Dong (₫)' },
  { code: 'ZAR', name: 'South African Rand (R)' }
];

export const getCurrencySymbol = (currencyCode: string): string => {
  const code = (currencyCode || 'USD').toUpperCase();
  const symbols: { [key: string]: string } = {
    USD: '$', EUR: '€', GBP: '£', JPY: '¥', AUD: 'A$', CAD: 'C$',
    CHF: 'CHF', HKD: 'HK$', SGD: 'S$', SEK: 'kr', ARS: '$', BRL: 'R$',
    CLP: '$', CNY: '¥', COP: '$', CZK: 'Kč', DKK: 'kr', HUF: 'Ft',
    ILS: '₪', INR: '₹', KRW: '₩', MXN: '$', NOK: 'kr', NZD: 'NZ$',
    PEN: 'S/.', PLN: 'zł', RUB: '₽', THB: '฿', TRY: '₺', TWD: 'NT$',
    UAH: '₴', VND: '₫', ZAR: 'R'
  };
  return symbols[code] || '$';
};

const AdminSubscriptionPlans: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Form State
  const [planName, setPlanName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [currency, setCurrency] = useState<string>('USD');
  const [billingInterval, setBillingInterval] = useState<string>('monthly');
  const [status, setStatus] = useState<string>('active');
  const [features, setFeatures] = useState<string[]>([]);
  const [featureInput, setFeatureInput] = useState<string>('');

  // Price-change flow state
  const [priceChangeModal, setPriceChangeModal] = useState<boolean>(false);
  const [priceChangePlan, setPriceChangePlan] = useState<SubscriptionPlan | null>(null);
  const [priceChangeRequestedAmount, setPriceChangeRequestedAmount] = useState<string>('');
  const [priceChangeRequestedCurrency, setPriceChangeRequestedCurrency] = useState<string>('USD');
  const [priceChangePreview, setPriceChangePreview] = useState<any>(null);
  const [priceChangePreviewLoading, setPriceChangePreviewLoading] = useState<boolean>(false);
  const [priceChangeApplying, setPriceChangeApplying] = useState<boolean>(false);
  const [priceChangeOperationId, setPriceChangeOperationId] = useState<number | null>(null);
  const [priceChangeStatus, setPriceChangeStatus] = useState<any>(null);
  const [priceChangePollInterval, setPriceChangePollInterval] = useState<any>(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/subscription-plans/admin`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('skillseek_admin_token') || ''}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setPlans(data.plans || []);
      } else {
        throw new Error(data.message || 'Failed to load subscription plans');
      }
    } catch (err: any) {
      console.error('Error fetching subscription plans:', err);
      Swal.fire({
        title: 'Error!',
        text: err?.message || 'Could not fetch subscription plans from server.',
        icon: 'error',
        confirmButtonColor: '#4f46e5'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingPlan(null);
    setPlanName('');
    setDescription('');
    setAmount('');
    setCurrency('USD');
    setBillingInterval('monthly');
    setStatus('active');
    setFeatures(['Unlimited service listings', 'Verified badge display', 'Priority support']);
    setFeatureInput('');
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setPlanName(plan.plan_name);
    setDescription(plan.description || '');
    setAmount(plan.amount.toString());
    setCurrency(plan.currency || 'USD');
    setBillingInterval(plan.billing_interval || 'monthly');
    setStatus(plan.status || 'active');
    setFeatures(Array.isArray(plan.features) ? plan.features : []);
    setFeatureInput('');
    setIsModalOpen(true);
  };

  const handleAddFeature = () => {
    if (featureInput.trim()) {
      if (!features.includes(featureInput.trim())) {
        setFeatures([...features, featureInput.trim()]);
      }
      setFeatureInput('');
    }
  };

  const handleRemoveFeature = (indexToRemove: number) => {
    setFeatures(features.filter((_, index) => index !== indexToRemove));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!planName.trim()) {
      Swal.fire('Validation Error', 'Plan Name is required', 'warning');
      return;
    }

    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount) || numericAmount < 0) {
      Swal.fire('Validation Error', 'Please enter a valid price amount', 'warning');
      return;
    }

    setSubmitting(true);

    const payload = {
      plan_name: planName.trim(),
      description: description.trim(),
      amount: numericAmount,
      currency,
      billing_interval: billingInterval,
      features,
      status
    };

    try {
      const url = editingPlan 
        ? `${API_BASE_URL}/subscription-plans/${editingPlan.id}`
        : `${API_BASE_URL}/subscription-plans`;

      const method = editingPlan ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('skillseek_admin_token') || ''}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      // requiresPriceChangeFlow = admin changed price on a real Paddle plan
      if ((response.status === 409 || data.requiresPriceChangeFlow) && editingPlan) {
        setIsModalOpen(false);
        // Open the price-change modal automatically
        setPriceChangePlan(editingPlan);
        setPriceChangeRequestedAmount(amount);
        setPriceChangeRequestedCurrency(currency);
        setPriceChangePreview(null);
        setPriceChangeOperationId(null);
        setPriceChangeStatus(null);
        setPriceChangeModal(true);
        // Immediately fetch preview
        fetchPriceChangePreview(editingPlan.id, amount, currency, billingInterval);
        setSubmitting(false);
        return;
      }

      if (data.success) {
        setIsModalOpen(false);
        Swal.fire({
          title: 'Success!',
          text: data.message || (editingPlan ? 'Plan updated successfully' : 'Plan created successfully'),
          icon: 'success',
          confirmButtonColor: '#4f46e5'
        });
        fetchPlans();
      } else {
        throw new Error(data.message || 'Operation failed');
      }
    } catch (err: any) {
      console.error('Error saving subscription plan:', err);
      Swal.fire({
        title: 'Error!',
        text: err?.message || 'Failed to save subscription plan.',
        icon: 'error',
        confirmButtonColor: '#4f46e5'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Price-Change Flow
  // ---------------------------------------------------------------------------

  const fetchPriceChangePreview = async (planId: number, newAmount: string, newCurrency: string, newInterval: string) => {
    setPriceChangePreviewLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/subscription-plans/${planId}/price-change/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('skillseek_admin_token') || ''}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ amount: parseFloat(newAmount), currency: newCurrency, billingInterval: newInterval })
      });
      const data = await res.json();
      if (data.success) {
        setPriceChangePreview(data);
      } else {
        Swal.fire('Preview Error', data.message || 'Preview failed', 'error');
      }
    } catch (err: any) {
      Swal.fire('Preview Error', err?.message || 'Failed to fetch price change preview', 'error');
    } finally {
      setPriceChangePreviewLoading(false);
    }
  };

  const handleApplyPriceChange = async () => {
    if (!priceChangePlan) return;
    const confirm = await Swal.fire({
      title: 'Apply Price Change?',
      html: `<p>The price of <strong>${priceChangePlan.plan_name}</strong> will be updated from <strong>${getCurrencySymbol(priceChangePlan.currency)}${priceChangePlan.amount.toFixed(2)}</strong> to <strong>${getCurrencySymbol(priceChangeRequestedCurrency)}${parseFloat(priceChangeRequestedAmount).toFixed(2)}</strong>.</p><p class="text-muted" style="font-size:13px">Active subscribers will be billed at the new rate at their <strong>next billing period</strong>. No immediate charge will occur.</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Apply Price Change',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#4f46e5'
    });
    if (!confirm.isConfirmed) return;

    setPriceChangeApplying(true);
    try {
      const res = await fetch(`${API_BASE_URL}/subscription-plans/${priceChangePlan.id}/price-change`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('skillseek_admin_token') || ''}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(priceChangeRequestedAmount),
          currency: priceChangeRequestedCurrency,
          billingInterval: priceChangePlan.billing_interval
        })
      });
      const data = await res.json();
      if (data.success && data.operationId) {
        setPriceChangeOperationId(data.operationId);
        // Start polling
        const interval = setInterval(() => {
          pollPriceChangeStatus(data.operationId);
        }, 3000);
        setPriceChangePollInterval(interval);
        pollPriceChangeStatus(data.operationId);
      } else {
        throw new Error(data.message || 'Failed to start price change');
      }
    } catch (err: any) {
      Swal.fire('Error', err?.message || 'Failed to apply price change', 'error');
    } finally {
      setPriceChangeApplying(false);
    }
  };

  const pollPriceChangeStatus = async (operationId: number) => {
    try {
      const res = await fetch(`${API_BASE_URL}/subscription-plans/price-change/${operationId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('skillseek_admin_token') || ''}` }
      });
      const data = await res.json();
      if (data.success) {
        setPriceChangeStatus(data);
        // Stop polling when done
        if (['completed', 'partially_completed', 'failed'].includes(data.status)) {
          setPriceChangePollInterval((prev: any) => { clearInterval(prev); return null; });
          fetchPlans();
        }
      }
    } catch (_) {}
  };

  const handleRetryFailedPriceChange = async () => {
    if (!priceChangeOperationId) return;
    try {
      await fetch(`${API_BASE_URL}/subscription-plans/price-change/${priceChangeOperationId}/retry`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('skillseek_admin_token') || ''}` }
      });
      // Resume polling
      const interval = setInterval(() => { pollPriceChangeStatus(priceChangeOperationId); }, 3000);
      setPriceChangePollInterval(interval);
    } catch (err: any) {
      Swal.fire('Error', err?.message || 'Failed to retry', 'error');
    }
  };

  const closePriceChangeModal = () => {
    clearInterval(priceChangePollInterval);
    setPriceChangeModal(false);
    setPriceChangePlan(null);
    setPriceChangePreview(null);
    setPriceChangeOperationId(null);
    setPriceChangeStatus(null);
  };

  const handleToggleStatus = async (plan: SubscriptionPlan) => {
    const isActivating = plan.status !== 'active';
    const actionText = isActivating ? 'Activate' : 'Deactivate';

    const confirmResult = await Swal.fire({
      title: `${actionText} Subscription Plan?`,
      text: isActivating 
        ? `Mark "${plan.plan_name}" as active? New users will be able to subscribe to this plan.`
        : `Mark "${plan.plan_name}" as inactive? Existing subscribers will continue normally, but new users won't see this plan.`,
      icon: isActivating ? 'question' : 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, ${actionText}`,
      cancelButtonText: 'Cancel',
      confirmButtonColor: isActivating ? '#16a34a' : '#ef4444'
    });

    if (!confirmResult.isConfirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/subscription-plans/${plan.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('skillseek_admin_token') || ''}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        Swal.fire('Success', `Subscription plan status set to ${isActivating ? 'active' : 'inactive'}.`, 'success');
        fetchPlans();
      } else {
        throw new Error(data.message || `Failed to ${actionText.toLowerCase()} plan`);
      }
    } catch (err: any) {
      console.error('Error toggling plan status:', err);
      Swal.fire('Error', err?.message || `Could not ${actionText.toLowerCase()} plan.`, 'error');
    }
  };

  return (
    <AdminLayout title="Subscription Plans" subtitle="Membership & Subscription Plan Management">
      <div className="container-fluid p-0">

        {/* Top Header Card */}
        <div style={{
          background: 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)',
          borderRadius: '16px',
          padding: '24px 32px',
          color: '#ffffff',
          marginBottom: '28px',
          boxShadow: '0 10px 25px -5px rgba(79, 70, 229, 0.4)'
        }} className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h3 style={{ margin: 0, fontWeight: 700, fontSize: '24px' }} className="d-flex align-items-center gap-2">
              <Zap size={28} /> Subscription Plans
            </h3>
            <p style={{ margin: '6px 0 0 0', opacity: 0.9, fontSize: '14px' }}>
              Create and manage talent subscription tiers, pricing, and feature allowances.
            </p>
          </div>
          <button 
            onClick={handleOpenCreateModal}
            className="d-flex align-items-center gap-2 px-4 py-2"
            style={{ 
              backgroundColor: '#ffffff', 
              color: '#4f46e5', 
              fontWeight: 700, 
              fontSize: '15px',
              borderRadius: '10px', 
              border: 'none', 
              boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
              cursor: 'pointer'
            }}
          >
            <Plus size={20} style={{ color: '#4f46e5' }} /> Create New Plan
          </button>
        </div>

        {/* Stats Row */}
        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <div className="card border-0 shadow-sm p-3" style={{ borderRadius: '12px', background: '#fff' }}>
              <div className="d-flex align-items-center gap-3">
                <div style={{ background: '#eef2ff', color: '#4f46e5', padding: '12px', borderRadius: '10px' }}>
                  <Layers size={24} />
                </div>
                <div>
                  <div className="text-muted small fw-semibold">Total Plans</div>
                  <h4 className="fw-bold m-0">{plans.length}</h4>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card border-0 shadow-sm p-3" style={{ borderRadius: '12px', background: '#fff' }}>
              <div className="d-flex align-items-center gap-3">
                <div style={{ background: '#ecfdf5', color: '#10b981', padding: '12px', borderRadius: '10px' }}>
                  <CheckCircle2 size={24} />
                </div>
                <div>
                  <div className="text-muted small fw-semibold">Active Plans</div>
                  <h4 className="fw-bold m-0">{plans.filter(p => p.status === 'active').length}</h4>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card border-0 shadow-sm p-3" style={{ borderRadius: '12px', background: '#fff' }}>
              <div className="d-flex align-items-center gap-3">
                <div style={{ background: '#fef3c7', color: '#f59e0b', padding: '12px', borderRadius: '10px' }}>
                  <Shield size={24} />
                </div>
                <div>
                  <div className="text-muted small fw-semibold">Plan Status</div>
                  <span className="badge bg-success">Active & Synced</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Loading Spinner */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}></div>
            <p className="mt-2 text-muted fw-semibold">Loading subscription plans...</p>
          </div>
        ) : plans.length === 0 ? (
          /* Empty State */
          <div className="card border-0 shadow-sm text-center py-5 px-4" style={{ borderRadius: '16px', background: '#fff' }}>
            <Layers size={56} className="text-muted mx-auto mb-3" style={{ opacity: 0.4 }} />
            <h5 className="fw-bold">No Subscription Plans Found</h5>
            <p className="text-muted">Click "Create New Plan" above to create your first Paddle-linked subscription plan.</p>
            <div>
              <button 
                onClick={handleOpenCreateModal} 
                className="px-4 py-2" 
                style={{ 
                  backgroundColor: '#4f46e5', 
                  color: '#ffffff', 
                  fontWeight: 600, 
                  fontSize: '14px',
                  borderRadius: '8px', 
                  border: 'none',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Plus size={18} /> Create Plan
              </button>
            </div>
          </div>
        ) : (
          /* Plans Cards Grid */
          <div className="row g-4">
            {plans.map((plan) => (
              <div className="col-lg-4 col-md-6" key={plan.id}>
                <div 
                  className="card border-0 shadow-sm h-100 position-relative" 
                  style={{ 
                    borderRadius: '16px', 
                    overflow: 'hidden',
                    borderTop: plan.status === 'active' ? '4px solid #4f46e5' : '4px solid #9ca3af',
                    background: '#fff'
                  }}
                >
                  <div className="card-body p-4 d-flex flex-column">

                    {/* Status & Interval Badges */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className={`badge ${plan.status === 'active' ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '12px', padding: '6px 12px', borderRadius: '20px' }}>
                        {plan.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                      <span className="badge bg-light text-dark border" style={{ fontSize: '12px', padding: '6px 12px', borderRadius: '20px' }}>
                        <Calendar size={13} className="me-1" />
                        {plan.billing_interval === 'hourly' ? 'Hourly Billing' :
                         plan.billing_interval === 'daily' ? 'Daily Billing' :
                         plan.billing_interval === 'weekly' ? 'Weekly Billing' :
                         plan.billing_interval === 'yearly' ? 'Yearly Billing' : 'Monthly Billing'}
                      </span>
                    </div>

                    {/* Plan Title & Description */}
                    <h4 className="fw-bold mb-1" style={{ color: '#1f2937' }}>{plan.plan_name}</h4>
                    <p className="text-muted small mb-3" style={{ minHeight: '38px' }}>
                      {plan.description || 'No description provided.'}
                    </p>

                    {/* Price Header */}
                    <div className="bg-light p-3 rounded-3 mb-3 d-flex align-items-baseline gap-1">
                      <span className="fs-2 fw-bolder text-primary">{getCurrencySymbol(plan.currency)}{plan.amount.toFixed(2)}</span>
                      <span className="text-muted fw-semibold">/{plan.currency} ({
                        plan.billing_interval === 'hourly' ? 'hr' :
                        plan.billing_interval === 'daily' ? 'day' :
                        plan.billing_interval === 'weekly' ? 'wk' :
                        plan.billing_interval === 'yearly' ? 'yr' : 'mo'
                      })</span>
                    </div>

                    {/* Dynamic Features List */}
                    <div className="mb-4 flex-grow-1">
                      <div className="fw-semibold small text-muted text-uppercase mb-2" style={{ letterSpacing: '0.5px' }}>
                        Plan Features
                      </div>
                      {Array.isArray(plan.features) && plan.features.length > 0 ? (
                        <ul className="list-unstyled mb-0">
                          {plan.features.map((feat, idx) => (
                            <li key={idx} className="d-flex align-items-center gap-2 mb-2 small">
                              <Check size={16} className="text-success flex-shrink-0" />
                              <span>{feat}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-muted small italic">No features listed</span>
                      )}
                    </div>

                    {/* Card Action Buttons - Professional Enterprise Control Bar */}
                    <div className="border-top pt-3 mt-auto">
                      <div className="d-flex gap-2 align-items-center mt-2">
                        <button 
                          onClick={() => handleOpenEditModal(plan)} 
                          className="btn flex-grow-1 d-flex align-items-center justify-content-center gap-2 fw-semibold"
                          style={{ 
                            borderRadius: '10px', 
                            padding: '8px 14px',
                            fontSize: '13px',
                            backgroundColor: '#f8fafc',
                            color: '#334155',
                            border: '1px solid #cbd5e1',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#e2e8f0';
                            e.currentTarget.style.color = '#0f172a';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8fafc';
                            e.currentTarget.style.color = '#334155';
                          }}
                        >
                          <Edit size={14} style={{ color: '#4f46e5' }} /> Edit Plan
                        </button>

                        {plan.status === 'active' ? (
                          <button 
                            onClick={() => handleToggleStatus(plan)} 
                            className="btn flex-grow-1 d-flex align-items-center justify-content-center gap-2 fw-semibold"
                            style={{ 
                              borderRadius: '10px', 
                              padding: '8px 14px',
                              fontSize: '13px',
                              backgroundColor: '#fef2f2',
                              color: '#dc2626',
                              border: '1px solid #fecaca',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#fee2e2';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#fef2f2';
                            }}
                          >
                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ef4444', display: 'inline-block' }}></span>
                            Deactivate
                          </button>
                        ) : (
                          <button 
                            onClick={() => handleToggleStatus(plan)} 
                            className="btn flex-grow-1 d-flex align-items-center justify-content-center gap-2 fw-semibold"
                            style={{ 
                              borderRadius: '10px', 
                              padding: '8px 14px',
                              fontSize: '13px',
                              backgroundColor: '#f0fdf4',
                              color: '#16a34a',
                              border: '1px solid #bbf7d0',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#dcfce7';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#f0fdf4';
                            }}
                          >
                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#22c55e', display: 'inline-block' }}></span>
                            Activate
                          </button>
                        )}
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal for Create / Edit Plan */}
        {isModalOpen && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(15, 23, 42, 0.75)',
              backdropFilter: 'blur(4px)',
              zIndex: 1040,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setIsModalOpen(false);
            }}
          >
            <div 
              style={{
                backgroundColor: '#ffffff',
                color: '#0f172a',
                width: '100%',
                maxWidth: '740px',
                maxHeight: '88vh',
                borderRadius: '16px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                border: '1px solid #e2e8f0',
                position: 'relative',
                zIndex: 1041
              }}
            >
              {/* Header */}
              <div 
                style={{
                  background: 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)',
                  color: '#ffffff',
                  padding: '18px 24px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexShrink: 0
                }}
              >
                <h5 style={{ margin: 0, fontWeight: 700, fontSize: '18px', color: '#ffffff' }} className="d-flex align-items-center gap-2">
                  <Zap size={22} />
                  {editingPlan ? 'Edit Subscription Plan' : 'Create New Subscription Plan'}
                </h5>
                <button 
                  type="button" 
                  onClick={() => setIsModalOpen(false)}
                  style={{ background: 'transparent', border: 'none', color: '#ffffff', cursor: 'pointer', opacity: 0.9 }}
                  disabled={submitting}
                >
                  <X size={22} />
                </button>
              </div>

              {/* Form & Body */}
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', margin: 0 }}>
                <div style={{ padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#ffffff', color: '#0f172a' }}>

                  {/* Subscription Plan Notice */}
                  <div style={{ backgroundColor: '#eef2ff', color: '#3730a3', border: '1px solid #c7d2fe', borderRadius: '10px', padding: '12px 16px' }} className="d-flex align-items-start gap-2 mb-4">
                    <Info size={20} className="flex-shrink-0 mt-1" style={{ color: '#4f46e5' }} />
                    <div className="small" style={{ fontSize: '13px', lineHeight: 1.5 }}>
                      <strong>Plan Configuration:</strong> Configure the plan name, price, billing interval, and features. Active plans will immediately be available for talent subscription.
                    </div>
                  </div>

                  <div className="row g-3">
                    {/* Plan Name */}
                    <div className="col-md-8">
                      <label style={{ color: '#1e293b', fontWeight: 600, fontSize: '14px', marginBottom: '6px', display: 'block' }}>
                        Plan Name <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        placeholder="e.g. Pro Unlimited Tier"
                        value={planName}
                        onChange={(e) => setPlanName(e.target.value)}
                        style={{ backgroundColor: '#ffffff', borderColor: '#cbd5e1', color: '#0f172a', fontSize: '14px', padding: '10px 14px', borderRadius: '8px' }}
                        required
                      />
                    </div>

                    {/* Status */}
                    <div className="col-md-4">
                      <label style={{ color: '#1e293b', fontWeight: 600, fontSize: '14px', marginBottom: '6px', display: 'block' }}>
                        Status
                      </label>
                      <select 
                        className="form-select"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        style={{ backgroundColor: '#ffffff', borderColor: '#cbd5e1', color: '#0f172a', fontSize: '14px', padding: '10px 14px', borderRadius: '8px' }}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>

                    {/* Description */}
                    <div className="col-12">
                      <label style={{ color: '#1e293b', fontWeight: 600, fontSize: '14px', marginBottom: '6px', display: 'block' }}>
                        Description
                      </label>
                      <textarea 
                        className="form-control" 
                        rows={2} 
                        placeholder="Brief description of who this plan is for..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        style={{ backgroundColor: '#ffffff', borderColor: '#cbd5e1', color: '#0f172a', fontSize: '14px', padding: '10px 14px', borderRadius: '8px' }}
                      ></textarea>
                    </div>

                    {/* Price & Currency */}
                    <div className="col-md-4">
                      <label style={{ color: '#1e293b', fontWeight: 600, fontSize: '14px', marginBottom: '6px', display: 'block' }}>
                        Price Amount <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ backgroundColor: '#eef2ff', color: '#4f46e5', borderColor: '#cbd5e1', fontWeight: 700 }}>{getCurrencySymbol(currency)}</span>
                        <input 
                          type="number" 
                          step="0.01" 
                          min="0" 
                          className="form-control" 
                          placeholder="29.99"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          style={{ backgroundColor: '#ffffff', borderColor: '#cbd5e1', color: '#0f172a', fontSize: '14px', padding: '10px 14px' }}
                          required
                        />
                      </div>
                    </div>

                    <div className="col-md-4">
                      <label style={{ color: '#1e293b', fontWeight: 600, fontSize: '14px', marginBottom: '6px', display: 'block' }}>
                        Currency
                      </label>
                      <select 
                        className="form-select"
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                        style={{ backgroundColor: '#ffffff', borderColor: '#cbd5e1', color: '#0f172a', fontSize: '14px', padding: '10px 14px', borderRadius: '8px' }}
                      >
                        {PADDLE_CURRENCIES.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.code} - {c.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Billing Interval */}
                    <div className="col-md-4">
                      <label style={{ color: '#1e293b', fontWeight: 600, fontSize: '14px', marginBottom: '6px', display: 'block' }}>
                        Billing Interval
                      </label>
                      <select 
                        className="form-select"
                        value={billingInterval}
                        onChange={(e) => setBillingInterval(e.target.value)}
                        style={{ backgroundColor: '#ffffff', borderColor: '#cbd5e1', color: '#0f172a', fontSize: '14px', padding: '10px 14px', borderRadius: '8px' }}
                      >
                        <option value="hourly">Hourly</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>

                    {/* Application User Access Privileges Selector */}
                    <div className="col-12">
                      <label style={{ color: '#1e293b', fontWeight: 600, fontSize: '14px', marginBottom: '6px', display: 'block' }} className="d-flex align-items-center gap-2">
                        <Shield size={16} style={{ color: '#4f46e5' }} /> Application User Access & Privileges
                      </label>
                      <div className="p-3 bg-light rounded" style={{ border: '1px solid #cbd5e1' }}>
                        <p className="text-muted small mb-2" style={{ fontSize: '12px', color: '#64748b' }}>
                          Select application features and access permissions enabled for subscribers of this plan:
                        </p>
                        <div className="d-flex flex-wrap gap-2">
                          {APPLICATION_ACCESS_OPTIONS.map((opt) => {
                            const isSelected = features.some((f) => f.toLowerCase().trim() === opt.toLowerCase().trim());
                            return (
                              <button
                                key={opt}
                                type="button"
                                className="btn btn-sm d-flex align-items-center gap-1 fw-semibold"
                                style={{
                                  borderRadius: '20px',
                                  fontSize: '12px',
                                  padding: '6px 14px',
                                  backgroundColor: isSelected ? '#4f46e5' : '#ffffff',
                                  color: isSelected ? '#ffffff' : '#475569',
                                  border: isSelected ? '1px solid #4f46e5' : '1px solid #cbd5e1',
                                  boxShadow: isSelected ? '0 2px 6px rgba(79, 70, 229, 0.25)' : 'none',
                                  transition: 'all 0.15s ease'
                                }}
                                onClick={() => {
                                  if (isSelected) {
                                    setFeatures(features.filter((f) => f.toLowerCase().trim() !== opt.toLowerCase().trim()));
                                  } else {
                                    setFeatures([...features, opt]);
                                  }
                                }}
                              >
                                {isSelected ? <Check size={14} /> : <Plus size={14} />}
                                {opt}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Dynamic Features List Input */}
                    <div className="col-12">
                      <label style={{ color: '#1e293b', fontWeight: 600, fontSize: '14px', marginBottom: '6px', display: 'block' }}>
                        Custom Features
                      </label>
                      <div className="input-group mb-2">
                        <input 
                          type="text" 
                          className="form-control" 
                          placeholder="Add feature (e.g. Unlimited service listings)"
                          value={featureInput}
                          onChange={(e) => setFeatureInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddFeature(); } }}
                          style={{ backgroundColor: '#ffffff', borderColor: '#cbd5e1', color: '#0f172a', fontSize: '14px', padding: '10px 14px' }}
                        />
                        <button 
                          type="button" 
                          className="btn btn-outline-primary"
                          onClick={handleAddFeature}
                          style={{ borderRadius: '0 8px 8px 0', padding: '10px 16px' }}
                        >
                          <Plus size={18} /> Add
                        </button>
                      </div>

                      {/* Features Tags display */}
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {features.map((feat, idx) => (
                          <span 
                            key={idx} 
                            className="badge d-flex align-items-center gap-1 py-2 px-3" 
                            style={{ fontSize: '13px', borderRadius: '20px', backgroundColor: '#f1f5f9', color: '#1e293b', border: '1px solid #cbd5e1' }}
                          >
                            <span>{feat}</span>
                            <X 
                              size={14} 
                              style={{ cursor: 'pointer', color: '#64748b', marginLeft: '4px' }}
                              onClick={() => handleRemoveFeature(idx)} 
                            />
                          </span>
                        ))}
                      </div>
                    </div>

                  </div>
                </div>

                {/* Footer */}
                <div style={{ backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', padding: '16px 24px', flexShrink: 0 }} className="d-flex justify-content-end gap-2">
                  <button 
                    type="button" 
                    className="btn btn-secondary px-4" 
                    onClick={() => setIsModalOpen(false)}
                    disabled={submitting}
                    style={{ borderRadius: '8px', backgroundColor: '#94a3b8', borderColor: '#94a3b8', fontWeight: 600 }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary px-4 fw-semibold"
                    disabled={submitting}
                    style={{ borderRadius: '8px', backgroundColor: '#4f46e5', borderColor: '#4f46e5', fontWeight: 600 }}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Connecting to Paddle API...
                      </>
                    ) : (
                      editingPlan ? 'Update Plan' : 'Create Plan'
                    )}
                  </button>
                </div>
              </form>

            </div>
          </div>
        )}

      {/* ======================== PRICE CHANGE MODAL ======================== */}
      {priceChangeModal && priceChangePlan && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            backgroundColor: 'rgba(15, 23, 42, 0.8)', backdropFilter: 'blur(4px)',
            zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
          }}
        >
          <div style={{
            backgroundColor: '#fff', width: '100%', maxWidth: '680px',
            maxHeight: '90vh', borderRadius: '16px', overflow: 'hidden',
            boxShadow: '0 25px 60px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column',
            border: '1px solid #e2e8f0'
          }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)', color: '#fff', padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h5 style={{ margin: 0, fontWeight: 700, fontSize: '17px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <DollarSign size={20} /> Price Change — {priceChangePlan.plan_name}
              </h5>
              <button onClick={closePriceChangeModal} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', opacity: 0.85 }}>
                <X size={22} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>

              {/* Warning Banner */}
              <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px', display: 'flex', gap: '10px' }}>
                <AlertCircle size={20} style={{ color: '#d97706', flexShrink: 0, marginTop: '2px' }} />
                <div style={{ fontSize: '13px', color: '#92400e', lineHeight: 1.6 }}>
                  <strong>Paddle Price Change — Same Price ID Preserved</strong><br />
                  The existing Paddle Price ID <code style={{ background: '#fef3c7', padding: '1px 6px', borderRadius: '4px', fontSize: '12px' }}>{priceChangePlan.paddle_price_id}</code> will be <em>updated in-place</em> — no new Price ID will be created. Active subscribers will be billed at the new rate at their <strong>next renewal date</strong>. No immediate charges will occur.
                </div>
              </div>

              {/* Price Summary */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{ background: '#f1f5f9', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current Price</div>
                  <div style={{ fontSize: '24px', fontWeight: 800, color: '#dc2626' }}>
                    {getCurrencySymbol(priceChangePlan.currency)}{priceChangePlan.amount.toFixed(2)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>{priceChangePlan.currency} / {priceChangePlan.billing_interval}</div>
                </div>
                <div style={{ fontSize: '20px', color: '#4f46e5', fontWeight: 700 }}>→</div>
                <div style={{ background: '#eef2ff', border: '2px solid #4f46e5', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#4338ca', fontWeight: 600, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>New Price</div>
                  <div style={{ fontSize: '24px', fontWeight: 800, color: '#4f46e5' }}>
                    {getCurrencySymbol(priceChangeRequestedCurrency)}{parseFloat(priceChangeRequestedAmount).toFixed(2)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6366f1' }}>{priceChangeRequestedCurrency} / {priceChangePlan.billing_interval}</div>
                </div>
              </div>

              {/* Preview Results */}
              {priceChangePreviewLoading && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div className="spinner-border text-primary" style={{ width: '2rem', height: '2rem' }}></div>
                  <p style={{ marginTop: '10px', color: '#64748b', fontSize: '14px' }}>Loading preview from Paddle...</p>
                </div>
              )}

              {priceChangePreview && !priceChangePreviewLoading && (
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px', marginBottom: '20px' }}>
                  <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e293b', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <CheckCircle size={16} style={{ color: '#10b981' }} /> Preview Ready
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
                      <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>Active Subscribers</div>
                      <div style={{ fontSize: '22px', fontWeight: 800, color: '#1e293b' }}>{priceChangePreview.affectedSubscriptions ?? 0}</div>
                    </div>
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
                      <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>Immediate Charge</div>
                      <div style={{ fontSize: '18px', fontWeight: 800, color: '#10b981' }}>$0.00</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>Next billing period only</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Operation Progress */}
              {priceChangeStatus && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                  <div style={{ fontWeight: 700, fontSize: '14px', color: '#15803d', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {priceChangeStatus.status === 'completed' ? <CheckCircle size={16} /> :
                     priceChangeStatus.status === 'failed' ? <AlertCircle size={16} style={{ color: '#dc2626' }} /> :
                     <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />}
                    Operation Status: <span style={{ textTransform: 'capitalize', color: priceChangeStatus.status === 'completed' ? '#15803d' : priceChangeStatus.status === 'failed' ? '#dc2626' : '#d97706' }}>{priceChangeStatus.status}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', fontSize: '13px' }}>
                    <div style={{ textAlign: 'center', background: '#dcfce7', borderRadius: '8px', padding: '10px' }}>
                      <div style={{ fontSize: '20px', fontWeight: 800, color: '#16a34a' }}>{priceChangeStatus.subscriptions?.completed ?? 0}</div>
                      <div style={{ color: '#15803d', fontSize: '11px', fontWeight: 600 }}>Updated</div>
                    </div>
                    <div style={{ textAlign: 'center', background: '#fef9c3', borderRadius: '8px', padding: '10px' }}>
                      <div style={{ fontSize: '20px', fontWeight: 800, color: '#a16207' }}>{priceChangeStatus.subscriptions?.pending ?? 0}</div>
                      <div style={{ color: '#a16207', fontSize: '11px', fontWeight: 600 }}>Pending</div>
                    </div>
                    <div style={{ textAlign: 'center', background: '#fef2f2', borderRadius: '8px', padding: '10px' }}>
                      <div style={{ fontSize: '20px', fontWeight: 800, color: '#dc2626' }}>{priceChangeStatus.subscriptions?.failed ?? 0}</div>
                      <div style={{ color: '#dc2626', fontSize: '11px', fontWeight: 600 }}>Failed</div>
                    </div>
                  </div>
                  {/* Paddle price updated confirmation */}
                  {priceChangeStatus.paddlePriceUpdatedAt && (
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <CheckCircle size={13} style={{ color: '#10b981' }} />
                      Paddle Price ID <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: '3px' }}>{priceChangePlan.paddle_price_id}</code> updated at {new Date(priceChangeStatus.paddlePriceUpdatedAt).toLocaleTimeString()}
                    </div>
                  )}
                </div>
              )}

              {/* Retry button */}
              {priceChangeStatus && ['partially_completed', 'failed'].includes(priceChangeStatus.status) && (priceChangeStatus.subscriptions?.failed ?? 0) > 0 && (
                <button
                  onClick={handleRetryFailedPriceChange}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', fontWeight: 600, cursor: 'pointer', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '14px' }}
                >
                  <RefreshCw size={15} /> Retry {priceChangeStatus.subscriptions?.failed} Failed Subscriptions
                </button>
              )}

            </div>

            {/* Footer */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'flex-end', gap: '12px', flexShrink: 0 }}>
              <button
                onClick={closePriceChangeModal}
                style={{ padding: '9px 20px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontWeight: 600, cursor: 'pointer', fontSize: '14px' }}
              >
                {priceChangeOperationId ? 'Close' : 'Cancel'}
              </button>
              {!priceChangeOperationId && (
                <>
                  <button
                    onClick={() => fetchPriceChangePreview(priceChangePlan.id, priceChangeRequestedAmount, priceChangeRequestedCurrency, priceChangePlan.billing_interval)}
                    disabled={priceChangePreviewLoading}
                    style={{ padding: '9px 20px', borderRadius: '8px', border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', fontWeight: 600, cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <RefreshCw size={14} /> {priceChangePreviewLoading ? 'Loading...' : 'Refresh Preview'}
                  </button>
                  <button
                    onClick={handleApplyPriceChange}
                    disabled={priceChangeApplying || !priceChangePreview}
                    style={{ padding: '9px 24px', borderRadius: '8px', border: 'none', background: priceChangePreview ? '#4f46e5' : '#a5b4fc', color: '#fff', fontWeight: 700, cursor: priceChangePreview ? 'pointer' : 'not-allowed', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <DollarSign size={14} /> {priceChangeApplying ? 'Applying...' : 'Apply Price Change'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default AdminSubscriptionPlans;
